import React, { useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import BeatLoader from "react-spinners/BeatLoader";
import {
  Box,
  TextField,
  Checkbox,
  MenuItem,
  FormControl,
  Select,
} from "@mui/material";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { toast } from "react-toastify";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import "../../StudySetup.css";
import { useCallbackPrompt } from "../../../../../hooks/useCallbackPrompt";
import DialogBox from "../../../../../components/DialogBox";
import moment from "moment";
import {
  editEmailContent,
  getAllEmailTypeData,
  getAllKeyWordsData,
  getEmailContentById,
  getGetPatientStatusTypesData,
} from "../../../../../services/email_builder";
import {
  GetCountriesServices,
  GetDrugTypeListServices,
  GetProcedureHeadersPageServices,
  GetReportFieldValueServices,
  SaveReportService,
  UpdateReportService,
} from "../../../../../services/reports";

const CreateReport = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const reportId = location.state.reportId;
  const reportTitle = location.state.reportTitle;
  const procName = location.state.procName;

  const [load, setLoad] = React.useState(false);
  const [showDialog, setShowDialog] = React.useState(false);

  const [showPrompt, confirmNavigation, cancelNavigation] =
    useCallbackPrompt(showDialog);

  const [countryOptions, setCountryOptions] = React.useState([]);
  const [drugTypeOptions, setDrugTypeOptions] = React.useState([]);

  const [fieldsValues, setFieldsValues] = React.useState({});

  const [fieldData, setFieldData] = React.useState([]);

  const editEmailSchema = yup.object().shape(
    fieldData?.reduce((schema, field) => {
      schema[field.columnName] = yup
        .string()
        .required(`${field.columnName} is required`);

      return schema;
    }, {})
  );

  const {
    register,
    setValue,
    getValues,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    mode: "onChange",
    resolver: yupResolver(editEmailSchema),
  });

  const notify = () =>
    toast.success(
      `${"Form Updated Successfully"}`,
      {
        // theme: "colored",
        toastId: "form-creation",
      }
    );

  const requestFailed = (msg) =>
    toast.error(`${msg}`, {
      // theme: "colored",
      toastId: "requestFailed",
    });

  const isDrugType = fieldData?.find((item) => item.columnName === "DrugType");

  React.useEffect(() => {
    fetchFieldByProcName();
    fetchCountries();
    fetchDataForFieldBind();
  }, []);

  React.useEffect(() => {
    if (isDrugType) {
      fetchDrugTypeOptions();
    }
  }, [isDrugType]);

  const fetchFieldByProcName = async () => {
    try {
      setLoad(true);
      const res = await GetProcedureHeadersPageServices(procName);

      if (res.status) {
        setLoad(false);
        setFieldData(res?.data.result);
      }
    } catch (err) {
      setLoad(false);
      console.log("Error: ", err.message);
    }
  };

  const fetchCountries = async () => {
    try {
      setLoad(true);
      const res = await GetCountriesServices();

      if (res.status) {
        setLoad(false);
        setCountryOptions(res?.data.result);
      }
    } catch (err) {
      setLoad(false);
      console.log("Error: ", err.message);
    }
  };

  const fetchDrugTypeOptions = async () => {
    try {
      setLoad(true);
      const res = await GetDrugTypeListServices();
      if (res.status) {
        setLoad(false);
        setDrugTypeOptions(res?.data.result);
      }
    } catch (err) {
      setLoad(false);
      console.log("Error: ", err.message);
    }
  };

  const fetchDataForFieldBind = async () => {
    try {
      setLoad(true);
      const res = await GetReportFieldValueServices(procName);

      if (res.status) {
        setLoad(false);
        setFieldsValues(res.data.result[0] || {});
      }
    } catch (err) {
      setLoad(false);
      console.log("Error: ", err.message);
    }
  };

  const onSubmit = async (data) => {
    const {
      Country,
      SiteDepotNumber,
      Investigator,
      BlindedLotNumber,
      Expiry,
      AvailableAtDepot,
      DamagedAtSite,
      ShipmentInTemptaleQueue,
      DestroyedByDepot,
      AssignedToPatient,
      DiscrepancyAtSite,
      ReturnedByPatient,
      ReturnToDepot,
      DestroyedAtSite,
      DiscrepancyAtDepot,
      ReconciledAtDepot,
      ReplacedKit,
      InTransit,
      MissingAtSite,
      ReconciledByCRA,
      STTQApproved,
      ProblemKit,
      Missing,
      LostByPatient,
      QuarantinedInTransit,
      Damaged,
      AvailableAtSite,
      Expired,
      NotYetApproved,
      DrugType,
      LotNumber,
    } = data;

    const convertToInt = (value) => parseInt(value, 10) || 0;

    const requestData = {
      countryId: Country,
      reportId: reportId,
      siteDepotNumber: SiteDepotNumber,
      investigator: Investigator,
      blindedLotNumber: BlindedLotNumber ? BlindedLotNumber : "",
      expiry: Expiry,
      lotNumber: LotNumber ? LotNumber : "",
      drugType: DrugType,
      availableAtDepot: convertToInt(AvailableAtDepot),
      damagedAtSite: convertToInt(DamagedAtSite),
      shipmentInTemptaleQueue: convertToInt(ShipmentInTemptaleQueue),
      destroyedByDepot: convertToInt(DestroyedByDepot),
      assignedToPatient: convertToInt(AssignedToPatient),
      discrepancyAtSite: convertToInt(DiscrepancyAtSite),
      returnedByPatient: convertToInt(ReturnedByPatient),
      returnToDepot: convertToInt(ReturnToDepot),
      destroyedAtSite: convertToInt(DestroyedAtSite),
      discrepancyAtDepot: convertToInt(DiscrepancyAtDepot),
      reconciledAtDepot: convertToInt(ReconciledAtDepot),
      replacedKit: convertToInt(ReplacedKit),
      inTransit: convertToInt(InTransit),
      missingAtSite: convertToInt(MissingAtSite),
      reconciledByCRA: convertToInt(ReconciledByCRA),
      sttqApproved: convertToInt(STTQApproved),
      problemKit: convertToInt(ProblemKit),
      missing: convertToInt(Missing),
      lostByPatient: convertToInt(LostByPatient),
      quarantinedInTransit: convertToInt(QuarantinedInTransit),
      damaged: convertToInt(Damaged),
      availableAtSite: convertToInt(AvailableAtSite),
      expired: convertToInt(Expired),
      notYetApproved: convertToInt(NotYetApproved),
      createdAt: Expiry,
      isActive: data.isActive ?? true,
    };

    try {
      setLoad(true);
      setShowDialog(false);
      let res;

      res = await SaveReportService(requestData);
      if (res.status) {
        setLoad(false);
        fetchFieldByProcName();
        fetchDataForFieldBind();
        notify();
        navigate("/study-management/study-settings/selected-report");
      }
    } catch (err) {
      setLoad(false);
      console.log("Error: ", err.message);
      requestFailed(err.message);
    }
  };

  const handleCancel = (e) => {
    e.preventDefault();
    navigate("/study-management/study-settings/selected-report");
  };

  const labelStyles = {
    display: "flex",
    height: "30px",
    marginTop: "10px",
  };

  const textBoxBody = {
    marginLeft: "2px",
  };

  const textBoxStylesNew = {
    fontSize: 15,
    width: "320px",
    height: "5px",
    padding: "13px 14px",
  };

  const rowStyles = {
    marginTop: "2%",
  };

  return (
    <>
      {load ? (
        <>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "70vh",
            }}
          >
            <BeatLoader color="#3661eb" />
          </div>
        </>
      ) : (
        <div className="content-body">
          <p className="study-management-link" style={{ fontWeight: "600" }}>
            <Link to="/study-management">Manage Your Study</Link> |{" "}
            <Link to="/study-management/study-settings/reports">
              Report Layout
            </Link>{" "}
            |{" "}
            <Link to="/study-management/study-settings/selected-report">
              Selected Reports
            </Link>{" "}
            |{" "}
            <p className="study-edit-link">
              {reportTitle ? reportTitle : "Create Report"}
            </p>
          </p>

          <div>
            <DialogBox
              showDialog={showPrompt}
              confirmNavigation={confirmNavigation}
              cancelNavigation={cancelNavigation}
            />
          </div>

          <Row style={{ marginTop: "2%" }}>
            <Col md={6}>
              <div className="study-management-head-start">
                <p className="study-management-heading">
                  {reportTitle ? reportTitle : "Edit Report"}
                </p>
              </div>
            </Col>
            <Col md={6}></Col>
          </Row>

          <Box
            component="form"
            onSubmit={handleSubmit(onSubmit)}
            sx={{
              height: "auto",
              width: "100%",
            }}
            autoComplete="off"
          >
            {fieldData.map((field) => (
              <div key={field.columnName}>
                {field.columnName === "Country" ? (
                  <Row>
                    <Col md={2}>
                      <p className="descriptionLabel" style={labelStyles}>
                        Country <span className="error-highlight">*</span>
                      </p>
                    </Col>

                    <Col md={10}>
                      <FormControl sx={{ width: "350px" }}>
                        <Select
                          value={getValues(field.columnName) || ""}
                          name="country"
                          inputProps={{
                            "aria-label": "",
                            className: "custom-select-report-class",
                          }}
                          InputProps={{
                            className: "custom-second-div-class",
                          }}
                          MenuProps={{
                            className: "custom-menu-root",
                          }}
                          defaultValue={""}
                          {...register(field.columnName, {
                            onChange: (e) => {
                              setValue(field.columnName, e.target.value, {
                                shouldValidate: true,
                              });
                              setShowDialog(true);
                            },
                          })}
                        >
                          <MenuItem value="">
                            <em>None</em>
                          </MenuItem>
                          {countryOptions &&
                            countryOptions?.map((item, index) => (
                              <MenuItem key={index} value={item.id}>
                                {item.name}
                              </MenuItem>
                            ))}
                        </Select>
                      </FormControl>
                      {errors.Country && (
                        <>
                          <div className="createCaregiverFlexEnd">
                            <div className="createCaregiverError">
                              <span className="error-text">
                                {errors.Country.message}
                              </span>
                            </div>
                          </div>
                        </>
                      )}
                    </Col>
                  </Row>
                ) : field.columnName === "DrugType" ? (
                  <Row>
                    <Col md={2}>
                      <p className="descriptionLabel" style={labelStyles}>
                        DrugType <span className="error-highlight">*</span>
                      </p>
                    </Col>

                    <Col md={10}>
                      <FormControl sx={{ width: "350px" }}>
                        <Select
                          value={getValues(field.columnName) || ""}
                          name="country"
                          inputProps={{
                            "aria-label": "",
                            className: "custom-select-report-class",
                          }}
                          InputProps={{
                            className: "custom-second-div-class",
                          }}
                          MenuProps={{
                            className: "custom-menu-root",
                          }}
                          defaultValue={""}
                          {...register(field.columnName, {
                            onChange: (e) => {
                              setValue(field.columnName, e.target.value, {
                                shouldValidate: true,
                              });
                              setShowDialog(true);
                            },
                          })}
                        >
                          <MenuItem value="">
                            <em>None</em>
                          </MenuItem>
                          {drugTypeOptions &&
                            drugTypeOptions?.map((item, index) => (
                              <MenuItem key={index} value={item.id}>
                                {item.name}
                              </MenuItem>
                            ))}
                        </Select>
                      </FormControl>
                      {errors.DrugType && (
                        <>
                          <div className="createCaregiverFlexEnd">
                            <div className="createCaregiverError">
                              <span className="error-text">
                                {errors.DrugType.message}
                              </span>
                            </div>
                          </div>
                        </>
                      )}
                    </Col>
                  </Row>
                ) : field.dataType.includes("int") ? (
                  <Row>
                    <Col md={2}>
                      <p className="descriptionLabel" style={labelStyles}>
                        {field.columnName}{" "}
                        <span className="error-highlight">*</span>
                      </p>
                    </Col>
                    <Col md={10}>
                      <TextField
                        type="number"
                        value={getValues(field.columnName) || ""}
                        style={textBoxBody}
                        inputProps={{ style: textBoxStylesNew }}
                        {...register(field.columnName, {
                          onChange: (e) => {
                            setValue(field.columnName, e.target.value, {
                              shouldValidate: true,
                            });
                            setShowDialog(true);
                          },
                        })}
                      />
                      {errors[field.columnName] && (
                        <>
                          <div className="createCaregiverFlexEnd">
                            <div className="createCaregiverError">
                              <span className="error-text">
                                {errors[field.columnName].message}
                              </span>
                            </div>
                          </div>
                        </>
                      )}
                    </Col>
                  </Row>
                ) : field.dataType.includes("datetimeoffset") ? (
                  <Row>
                    <Col md={2}>
                      <p className="descriptionLabel" style={labelStyles}>
                        {field.columnName}{" "}
                        <span className="error-highlight">*</span>
                      </p>
                    </Col>
                    <Col md={10}>
                      <TextField
                        type="date"
                        value={getValues(field.columnName) || ""}
                        style={textBoxBody}
                        inputProps={{ style: textBoxStylesNew }}
                        {...register(field.columnName, {
                          onChange: (e) => {
                            setValue(field.columnName, e.target.value, {
                              shouldValidate: true,
                            });
                            setShowDialog(true);
                          },
                        })}
                      />
                      {errors[field.columnName] && (
                        <>
                          <div className="createCaregiverFlexEnd">
                            <div className="createCaregiverError">
                              <span className="error-text">
                                {errors[field.columnName].message}
                              </span>
                            </div>
                          </div>
                        </>
                      )}
                    </Col>
                  </Row>
                ) : (
                  <Row>
                    <Col md={2}>
                      <p className="descriptionLabel" style={labelStyles}>
                        {field.columnName}{" "}
                        <span className="error-highlight">*</span>
                      </p>
                    </Col>
                    <Col md={10}>
                      <TextField
                        style={textBoxBody}
                        value={getValues(field.columnName) || ""}
                        inputProps={{ style: textBoxStylesNew }}
                        {...register(field.columnName, {
                          onChange: (e) => {
                            setValue(field.columnName, e.target.value, {
                              shouldValidate: true,
                            });
                            setShowDialog(true);
                          },
                        })}
                      />

                      {errors[field.columnName] && (
                        <>
                          <div className="createCaregiverFlexEnd">
                            <div className="createCaregiverError">
                              <span className="error-text">
                                {errors[field.columnName].message}
                              </span>
                            </div>
                          </div>
                        </>
                      )}
                    </Col>
                  </Row>
                )}
              </div>
            ))}

            <Row style={rowStyles}>
              <Col md={8}></Col>
              <Col md={2}>
                <div className="study-management-head-end">
                  <button
                    onClick={(e) => {
                      handleCancel(e);
                    }}
                    className="study-management-cancel-btn"
                  >
                    Cancel
                  </button>
                </div>
              </Col>
              <Col md={2}>
                <div className="study-management-head-end">
                  <button
                    type="submit"
                    className="study-management-create-btn-md"
                  >
                    Save
                  </button>
                </div>
              </Col>
            </Row>
          </Box>
        </div>
      )}
    </>
  );
};

export default CreateReport;
